import { useEffect, useMemo, useRef, useState } from "react";
import { addDays, differenceInCalendarDays, format, getDaysInMonth, startOfYear } from "date-fns";
import api from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";

const iso = (d) => format(d, "yyyy-MM-dd");

export function monthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function rangeParams(mode, month) {
  if (mode === "week") return { start: iso(addDays(new Date(), -6)), days: 7 };
  if (mode === "year") {
    const now = new Date();
    const jan = startOfYear(now);
    return { start: iso(jan), days: differenceInCalendarDays(now, jan) + 1 };
  }
  return { start: iso(month), days: getDaysInMonth(month) };
}

/** Loads a site's energy dashboard for the selected range and keeps it fresh while it builds. */
export function useSiteEnergy(siteId, mode, month) {
  const [refreshing, setRefreshing] = useState(false);
  const params = useMemo(() => rangeParams(mode, month), [mode, month]);

  const query = useApiQuery(
    ["siteEnergy", siteId, params.start, params.days],
    `/sites/${encodeURIComponent(siteId)}/energy`,
    {
      params: { dashboard: true, ...params },
      // Poll fast while the server is still ingesting history, slowly once it is ready.
      refetchInterval: (q) => (q.state.data?.status === "building" ? 5000 : 60000),
      // Keep the last good summary on screen while a rebuild is in flight.
      placeholderData: (prev) => prev,
    }
  );

  const payload = query.data;
  const building = payload?.status === "building";
  const err = query.err || (payload?.status === "error"
    ? payload.detail || "Could not build the energy summary"
    : null);

  // A poll taken mid-ingest can come back without totals; keep the last complete summary on screen.
  const lastGood = useRef(null);
  useEffect(() => {
    if (payload?.totals) lastGood.current = payload;
  }, [payload]);

  const load = async (refresh = false) => {
    if (!refresh) return query.refetch();
    setRefreshing(true);
    try {
      await api.get(`/sites/${encodeURIComponent(siteId)}/energy`, {
        params: { dashboard: true, ...params, refresh: true },
      });
      return await query.refetch();
    } finally {
      setRefreshing(false);
    }
  };

  return {
    data: payload?.totals ? payload : lastGood.current,
    params,
    loading: query.loading,
    refreshing,
    building,
    err,
    load,
  };
}
